import { MapProjection, MapTile } from "@oreme/ui";
import { atom, createStore } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { atomFamily } from "jotai-family";
import { atomWithLocation } from "jotai-location";
import { Observatory, Site, Tag, ThemeTypeEnum } from "@/types";
import { atomWithToggle } from "@/utils/atom";
import { mapProjections, mapTiles } from "@/utils/map";

/**
 * Store
 */
function createSitesStore(
  initialObservatories: Observatory[],
  initialSites: Site[],
  initialTags: Tag[],
) {
  const store = createStore();
  store.set(observatoriesAtom, initialObservatories);
  store.set(sitesAtom, initialSites);
  store.set(tagsAtom, initialTags);

  return store;
}

const locationAtom = atomWithLocation();

/**
 * Observatory Atoms
 */
const _observatoryAtom = atom<Observatory | null>(null);
const observatoriesAtom = atom<Observatory[]>([]);
const selectedObservatoryAtom = atom(
  (get) => {
    return get(_observatoryAtom);
  },
  (get, set, newObservatory: Observatory | null) => {
    set(_observatoryAtom, newObservatory);
    if (newObservatory) {
      set(locationAtom, {
        pathname: "/sites",
        searchParams: new URLSearchParams([
          newObservatory ? ["monitoring", newObservatory.id] : [],
        ]),
      });
    }
  },
);
const filteredObservatoriesAtom = atom((get) => {
  const allObservatories = get(observatoriesAtom);
  const selectedTagIds = get(selectedTagIdsAtom);
  const themeVisibility = get(themeVisibilityAtom);

  let filteredObservatories = allObservatories;

  filteredObservatories = filteredObservatories.filter(
    (observatory) => themeVisibility[observatory.theme as ThemeTypeEnum],
  );

  if (selectedTagIds.length > 0) {
    filteredObservatories = filteredObservatories.filter((observatory) =>
      observatory.tags?.some((tag) => selectedTagIds.includes(tag.tagId)),
    );
  }

  return filteredObservatories;
});

/**
 * Site Atoms
 */

const sitesAtom = atom<Site[]>([]);
const selectedSiteAtom = atom<Site | null>(null);
const siteObservatoryAtom = atom((get) => {
  const observatories = get(observatoriesAtom);
  const site = get(selectedSiteAtom);

  if (site) {
    return observatories.find(
      (observatory) => observatory.id === site.observatoryId,
    );
  }

  return undefined;
});

const filteredSitesAtom = atom<Site[]>((get) => {
  const allSites = get(sitesAtom);
  const filteredObservatories = get(filteredObservatoriesAtom);
  const selectedObservatory = get(selectedObservatoryAtom);
  const themeVisibility = get(themeVisibilityAtom);

  let filteredSites = allSites;

  // Case 1: filter sites by selected observatory
  if (selectedObservatory) {
    filteredSites = filteredSites.filter(
      (site) => site.observatoryId === selectedObservatory.id,
    );
  }

  // Case 2: filter sites by filtered observatories
  filteredSites = filteredSites.filter((site) =>
    filteredObservatories.some(
      (observatory) => observatory.id === site.observatoryId,
    ),
  );

  // Case 3: filter sites  by theme visibility
  filteredSites = filteredSites.filter(
    (observatory) => themeVisibility[observatory.theme as ThemeTypeEnum],
  );

  return filteredSites;
});

const earthSitesAtom = atom((get) =>
  get(filteredSitesAtom).filter((site) => site.theme === ThemeTypeEnum.EARTH),
);

const waterSitesAtom = atom((get) =>
  get(filteredSitesAtom).filter((site) => site.theme === ThemeTypeEnum.WATER),
);

const universeSitesAtom = atom((get) =>
  get(filteredSitesAtom).filter(
    (site) => site.theme === ThemeTypeEnum.UNIVERSE,
  ),
);

const biodiversitySitesAtom = atom((get) =>
  get(filteredSitesAtom).filter(
    (site) => site.theme === ThemeTypeEnum.BIODIVERSITY,
  ),
);

const sitesCountersAtom = atom((get) => {
  const sites = get(filteredSitesAtom);
  const earthSites = get(earthSitesAtom);
  const waterSites = get(waterSitesAtom);
  const universeSites = get(universeSitesAtom);
  const biodiversitySites = get(biodiversitySitesAtom);

  const counters: {
    total: number;
    byTheme: Record<string, number>;
  } = {
    total: sites.length,
    byTheme: {
      earth: earthSites.length,
      water: waterSites.length,
      universe: universeSites.length,
      biodiversity: biodiversitySites.length,
    },
  };

  return counters;
});

/**
 * Tag Atoms
 */

const tagsAtom = atom<Tag[]>([]);
const selectedTagIdsAtom = atom<number[]>([]);
const suggestedTagsAtom = atom<Tag[]>([]);

const favouriteTagsAtom = atom((get) =>
  get(tagsAtom).filter((tag) => tag.isFavourite),
);

/**
 * Theme Atoms
 */
const themeKeys = [
  ThemeTypeEnum.EARTH,
  ThemeTypeEnum.WATER,
  ThemeTypeEnum.BIODIVERSITY,
  ThemeTypeEnum.UNIVERSE,
] as const;

const themeVisibilityAtomFamily = atomFamily(() => atomWithToggle(true));

const themeVisibilityAtom = atom((get) => {
  const values: Record<(typeof themeKeys)[number], boolean> = {} as Record<
    ThemeTypeEnum,
    boolean
  >;

  themeKeys.forEach((key) => {
    values[key] = get(themeVisibilityAtomFamily(key));
  });

  return values;
});

const setSingThemeVisibilityAtom = atom(
  null,
  (get, set, layerKey: ThemeTypeEnum) => {
    // Désactive toutes les couches
    themeKeys.forEach((key) => {
      set(themeVisibilityAtomFamily(key), false);
    });
    // Active uniquement la couche spécifiée
    set(themeVisibilityAtomFamily(layerKey), true);
  },
);

/**
 * Map Atoms
 */

const mapTileAtom = atom<MapTile>(mapTiles.get("osm-openmaptiles") as MapTile);
const mapProjectionAtom = atom<MapProjection>(
  mapProjections.get("globe") as MapProjection,
);

/**
 * Layout Atoms
 */
const sidebarVisibilityAtom = atomWithToggle(true);
const sidebarFilterTagVisibilityAtom = atomWithToggle(false);
const sidebarFilterLocationVisibilityAtom = atomWithToggle(false);
const sidebarFilterObservatoryVisibilityAtom = atomWithToggle(false);
const activePanelAtom = atom<string | null>(null);
const panelTagSearchVisibilityAtom = atomWithToggle(false);
const panelRegionSearchVisibilityAtom = atomWithToggle(false);
const settingsVisibilityAtom = atomWithToggle(false);
const observatoryListVisibilityAtom = atomWithToggle(false);
const tourVisibilityAtom = atomWithStorage("sites-tour", true);

export {
  createSitesStore,
  locationAtom,
  observatoriesAtom,
  selectedObservatoryAtom,
  filteredObservatoriesAtom,
  sitesAtom,
  selectedSiteAtom,
  siteObservatoryAtom,
  filteredSitesAtom,
  earthSitesAtom,
  waterSitesAtom,
  universeSitesAtom,
  biodiversitySitesAtom,
  sitesCountersAtom,
  tagsAtom,
  selectedTagIdsAtom,
  suggestedTagsAtom,
  favouriteTagsAtom,
  themeVisibilityAtomFamily,
  themeVisibilityAtom,
  mapTileAtom,
  mapProjectionAtom,
  setSingThemeVisibilityAtom,
  sidebarVisibilityAtom,
  sidebarFilterTagVisibilityAtom,
  sidebarFilterLocationVisibilityAtom,
  sidebarFilterObservatoryVisibilityAtom,
  activePanelAtom,
  panelTagSearchVisibilityAtom,
  panelRegionSearchVisibilityAtom,
  settingsVisibilityAtom,
  observatoryListVisibilityAtom,
  tourVisibilityAtom,
};
